import { createEvent, restore, sample } from 'effector'
import { fx, path } from '../../../libs'
import { Error, Gateway, GatewayExchanger } from './types'
import { toPath } from '../../../features/router'
import { getExchangers } from '../../exchanger/list/model'
import { getWithdrawalGroups } from '../../withdrawalGroups/list/model'

export const exchangerDataMounted = createEvent<{ id: string }>()

export const gatewayMounted = createEvent()
export const setGateway = createEvent<Gateway>()
export const setGatewayExchanger = createEvent<GatewayExchanger>()
export const setError = createEvent<Error>()
export const submitSaveGateway = createEvent<Gateway>()
export const setWithdrawalGroupsOption =
  createEvent<{ text: string; value: number }[]>()
export const setExchangerOption = createEvent<{ id: number; value: string }[]>()
export const $exchangerOptions = restore(setExchangerOption, [
  {
    id: 0,
    value: '',
  },
])
export const $withdrawalGroups = restore(setWithdrawalGroupsOption, [
  {
    text: '',
    value: 0,
  },
])
export const $gateway = restore(setGateway, {
  gatewayId: 0,
  exchangerId: 0,
  withdrawalGroupId: 0,
  name: '',
  internalName: '',
  praxisGatewayHash: '',
  logo: '',
  file: '',
  psCode: '',
  paymentMethodType: '',
  processingProvider: '',
  depositVerificationRequired: false,
  withdrawalGroup: {
    id: 0,
    name: '',
  },
  praxisGateway: {
    systemName: '',
    status: '',
    image: '',
    is3D: false,
    isDepositSupported: false,
    isPayoutSupported: false,
    isAuthorizationSupported: false,
    isRefundSupported: false,
  },
  technicalCountryCodeA3: null,
  technicalMethodKind: null,
})

export const $gatewayExchanger = restore(setGatewayExchanger, {
  ownerId: 0,
  isPartnerOnly: false,
  description: '',
  url: '',
})

export const $error = restore(setError, {
  message: '',
  code: 0,
})

export const getGateway = fx<void, Gateway>({
  method: 'GET',
  url: '/gateway/',
})

export const createGateway = fx<Gateway, Gateway>({
  method: 'POST',
  url: '/gateway/',
})

sample({
  clock: [createGateway.doneData],
  fn: (doneData) => {
    toPath(path.billing.gateway.entry())
  },
})

sample({
  clock: submitSaveGateway,
  fn: (gateway) => {
    return {
      url: `/gateway`,
      formData: { ...gateway },
    }
  },
  target: createGateway,
})

sample({
  clock: [getGateway.failData, createGateway.failData],
  fn: (failData) => {
    return { message: failData.body.message, code: failData.body.code }
  },
  target: [setError],
})

sample({
  clock: exchangerDataMounted,
  fn: ({ id }) => {
    let query = {}

    // @ts-ignore
    query['where'] = `{"ownerId": "${id}"}`

    // @ts-ignore
    query['populate'] = `${JSON.stringify(['Exchanger.owner'])}`
    return {
      query,
    }
  },
  target: getExchangers,
})

sample({
  clock: getExchangers.doneData,
  fn: (doneData) => {
    const exchangers = doneData.body.items

    return exchangers.map((item) => ({
      id: item.id,
      value: item.owner.fullName + ' (' + item.owner.clientId + ')',
    }))
  },
  target: setExchangerOption,
})

sample({
  clock: gatewayMounted,
  fn: () => {
    let query = {}
    return {
      query,
    }
  },
  target: getWithdrawalGroups,
})

sample({
  clock: getWithdrawalGroups.doneData,
  fn: (doneData) => {
    const wg = doneData.body.items

    return wg.map((item) => ({
      text: item.name,
      value: item.id,
    }))
  },
  target: setWithdrawalGroupsOption,
})

$error
  .on(gatewayMounted, () => {
    return {
      message: '',
      code: 0,
    }
  })
  .on(submitSaveGateway, () => {
    return {
      message: '',
      code: 0,
    }
  })
